import { ImageFileSystemRecord } from "@/shared/sync/mediaRepository/image_filesystem";
import { additionalDataIsSame } from "../mediaRepository/vfsHelpers";
import { VirtualFileSystem } from "../mediaRepository/VirtualFileSystem";
import { FileSystemRecord } from "../mediaRepository/FileSystemRecord";
import { UrlHelpers } from "@/shared/services/UrlHelpers";
import { FileMetadataEntry } from "@/utils/IndexedDB";

export class BtDiskFileUtils {
  static async deleteFiles(filePaths: string[]): Promise<void> {
    await VirtualFileSystem.getInstance().deleteAll(filePaths, true)
  }

  static resetDate(filePath: string, newDate: Date): boolean {
    VirtualFileSystem.getInstance().resetDate(filePath, newDate)
    return true;
  }

  static async writeFile(filePath: string, fileBlob: string): Promise<boolean> {
    try {
      await VirtualFileSystem.getInstance().writeBlobUrl(filePath, fileBlob)
      return true;
    } finally {
      if (fileBlob?.startsWith('blob:')) {
        UrlHelpers.revokeObjectURLSmart(fileBlob)
      }
    }
  }

  static async readFile(filePath: string): Promise<string | null>  {
    let record = await VirtualFileSystem.getInstance().get(filePath)
    if (!record || record.isFolder) return null;
    return record.getBlobUrl();
  }

  static async attributesOfItem(filePath: string): Promise<FileSystemRecord | null> {
    let record = await VirtualFileSystem.getInstance().get(filePath)
    return record
  }

  static async moveFiles(sourcePaths: string[], targetPaths: string[]): Promise<void> {
    if (sourcePaths.length !== targetPaths.length){
      throw new Error('Source and target paths arrays must have the same length.');
    }
    await Promise.all(
      sourcePaths.map((src, i) =>
          VirtualFileSystem.getInstance().move(src, targetPaths[i])
      )
    );
  }

  static allEmptyDirectories(path: string): string[]{
    return []
  }

  static async duplicate(sourcePaths: string[], targetPaths: string[]): Promise<void> {
    if (sourcePaths.length !== targetPaths.length){
      throw new Error('Source and target paths arrays must have the same length.');
    }
    for (let i = 0; i < sourcePaths.length; i++) {
      await VirtualFileSystem.getInstance().copy(sourcePaths[i], targetPaths[i])
    } 
    return Promise.resolve();
  }

  // Create hard links for specified files
  static async linkFiles(sourcePaths: string[], targetPaths: string[]): Promise<void> {
    if (sourcePaths.length !== targetPaths.length){
      throw new Error('Source and target paths arrays must have the same length.');
    }
    for (let i = 0; i < sourcePaths.length; i++) {
      await VirtualFileSystem.getInstance().link(sourcePaths[i], targetPaths[i])
    } 
    return Promise.resolve();
  }

  static async keepOnlyLatestVersion(folder: string, clearMemory: boolean): Promise<void> {
    let allFiles = await VirtualFileSystem.getInstance().list(folder)
    allFiles.sort((a, b) => new Date(b[1].undoDate).getTime() - new Date(a[1].undoDate).getTime());
    
    let deduplicatedRecords = this.deduplicateRecords(allFiles)
    deduplicatedRecords.sort((a, b) => new Date(b[1].undoDate).getTime() - new Date(a[1].undoDate).getTime());
    
    for (let file of allFiles){
      let url = file[0];
      if (url.indexOf('?') === -1) {
        continue
      }

      let shouldClearRecord = clearMemory;
      if (clearMemory) {
        let isDeduplicatedRecord = false
        for (let [deduplicatedUrl, deduplicatedRecord] of deduplicatedRecords) {
          isDeduplicatedRecord = additionalDataIsSame(deduplicatedRecord, file[1]);
          if (isDeduplicatedRecord) {
            break
          }
        }  
        shouldClearRecord = !isDeduplicatedRecord
      }
      
      await VirtualFileSystem.getInstance().delete(url, shouldClearRecord)
    }

    for (let [url, record] of deduplicatedRecords) {
      await VirtualFileSystem.getInstance().write(url, record)
    }
    return Promise.resolve();
  }

  static async filterDeletedLayers(folder: string, existingLayers: string[], clearMemory: boolean): Promise<void> {
    let allFiles = await VirtualFileSystem.getInstance().list(folder)

    function isMissingLayer(url: string): boolean {
      if (url.indexOf('Layers') === -1) {
        return false;
      }
      for (let existingLayer of existingLayers) {
        if(url.indexOf(existingLayer) !== -1) {
          return false;
        }
      }
      return  true;   
    }

    for (let file of allFiles){
      let url = file[0];
      let record = file[1];
      let isMissing = isMissingLayer(url);
      if (isMissing && !record.isFolder) {
          VirtualFileSystem.getInstance().delete(url, clearMemory)
      }
    }

    let allFilesAfterDeletion = await VirtualFileSystem.getInstance().list(folder)
    console.log(allFilesAfterDeletion);

    return Promise.resolve();
  }

  static deduplicateRecords(items: [url: string, record: FileSystemRecord][]): [url: string, record: FileSystemRecord][] {
    const recordMap: Map<string, FileSystemRecord> = new Map();

    items.forEach(([url, record]) => {
        // Remove the query string from the URL
        const urlWithoutQueryString = url.split('?')[0];

        // Check if the URL (without query string) already exists in the map
        if (recordMap.has(urlWithoutQueryString)) {
            const existingRecord = recordMap.get(urlWithoutQueryString)!;

            // Compare the undodDate and update the record if the current one is newer
            if (new Date(record.undoDate) > new Date(existingRecord.undoDate)) {
                recordMap.set(urlWithoutQueryString, record);
            }
        } else {
            // If not in the map, add the current URL and record
            recordMap.set(urlWithoutQueryString, record);
        }
    });

    // Convert the map back to an array of tuples [url, record]
    return Array.from(recordMap).map(([url, record]) => [url, record]);
}



  // Create a folder if it doesn't exist
  static async createFolderIfNone(folderPath: string): Promise<Boolean> {
    VirtualFileSystem.getInstance().createFolderIfNone(folderPath)
    return Promise.resolve(true);
  }
  
  static async exist(folderPath: string): Promise<Boolean> {
    let result = VirtualFileSystem.getInstance().exist(folderPath)
    return Promise.resolve(result);
  }

  static async exists(url: string): Promise<boolean> {
    const items = await this.list(url);
    return items.length > 0;
  }

  static async list(url: string): Promise<[url: string, record: FileSystemRecord][]> {
    const records = await VirtualFileSystem.getInstance().list(url);
    return records;
  }

  // Metadata-only, files-only listing that never deserializes blob bytes — use for
  // building the sync file index instead of list() (which hydrates full records).
  static async listMetadata(url: string): Promise<FileMetadataEntry[]> {
    return VirtualFileSystem.getInstance().listMetadata(url);
  }
}